import { PropsWithChildren, ReactElement, useEffect } from "react";

import { ErrorBoundary } from "@rollbar/react";
import { useRouter } from "next/navigation";
import { useLocalStorage } from "usehooks-ts";

import { Link, Text, useDisclosure } from "@chakra-ui/react";

import ConfirmModal from "@/components/shared/ConfirmModal";
import ErrorFallback from "@/components/shared/ErrorFallback";
import WorkspaceSidebar from "@/components/WorkspaceSidebar";
import { layoutModes } from "@/constants/layoutModes";
import { NavMenuItemSlug } from "@/constants/navMenus";
import localStorageKeys from "@/enums/localStorageKeys";
import useReadySearchParams from "@/hooks/useReadySearchParams";
import { useWorkspace } from "@/providers/useWorkspace";
import { applicationsPath, subscriptionPath } from "@/utils/pathGenerators";

import CommonLayout, { CommonLayoutProps } from "./CommonLayout";

function WorkspaceLayout({
  mode = layoutModes.default,
  selectedItem,
  children,
  ...props
}: PropsWithChildren<CommonLayoutProps & { selectedItem?: NavMenuItemSlug }>): ReactElement | null {
  const [lastUsedWorkspaceId, setLastUsedWorkspaceId] = useLocalStorage(
    localStorageKeys.LAST_USED_WORKSPACE_ID,
    ""
  );
  const { replace } = useRouter();
  const { currentWorkspace, isSandbox } = useWorkspace();
  const expiringPlan = currentWorkspace?.expiringPlan;
  const expiredPlan = currentWorkspace?.expiredPlan;
  const [timeWarningSeen, setTimeWarningSeen] = useLocalStorage(localStorageKeys.TIME_WARNING_SEEN, false);
  const [expiredWarningSeen, setExpiredWarningSeen] = useLocalStorage(
    localStorageKeys.EXPIRED_WARNING_SEEN,
    false
  );

  const timeWarningDisclosureProps = useDisclosure();
  const expiredWarningDisclosureProps = useDisclosure();

  const { onOpen: timeWarningOnOpen, onClose: timeWarningOnClose } = timeWarningDisclosureProps;
  const { onOpen: expiredWarningOnOpen, onClose: expiredWarningOnClose } = expiredWarningDisclosureProps;

  const { workspaceId } = useReadySearchParams();
  useEffect(() => {
    if (!lastUsedWorkspaceId && workspaceId !== lastUsedWorkspaceId) {
      setLastUsedWorkspaceId(workspaceId);
    }
  }, [lastUsedWorkspaceId, setLastUsedWorkspaceId, workspaceId]);

  useEffect(() => {
    if (isSandbox && expiringPlan && !expiredPlan && !timeWarningSeen) {
      timeWarningOnOpen();
    }
  }, [isSandbox, expiringPlan, expiredPlan, timeWarningSeen, timeWarningOnOpen]);

  useEffect(() => {
    if (isSandbox && expiredPlan && !expiredWarningSeen) {
      replace(applicationsPath({ workspaceId }));
      expiredWarningOnOpen();
    }
  }, [isSandbox, expiredPlan, expiredWarningSeen, expiredWarningOnOpen, replace, workspaceId]);

  const handleTimeWarningClose = () => {
    setTimeWarningSeen(true);
    timeWarningOnClose();
  };

  const handleExpiredWarningClose = () => {
    setExpiredWarningSeen(true);
    expiredWarningOnClose();
  };

  const { selectedItem: modeSelectedItem, ...navMenuProps } = mode.navMenu;
  return (
    <>
      <CommonLayout
        // We are setting a key here so that when the workspaceId is
        // changed we remount the whole layout. This allows URLs generated
        // with our pathTo helper to regenerate with the correct workspaceId
        key={`${workspaceId}-layout`}
        LeftSidebar={<WorkspaceSidebar {...navMenuProps} selectedItem={selectedItem ?? modeSelectedItem} />}
        mode={mode}
        {...props}
      >
        <ErrorBoundary fallbackUI={ErrorFallback}>{children}</ErrorBoundary>
      </CommonLayout>
      <ConfirmModal
        {...timeWarningDisclosureProps}
        hideCancel
        description={
          <Text>
            You only have a few Environment Hours remaining. Please{" "}
            <Link href={subscriptionPath({ workspaceId })} onClick={handleTimeWarningClose}>
              upgrade your plan
            </Link>{" "}
            to continue without interruption
          </Text>
        }
        header="Low Environment Hours"
        onConfirm={handleTimeWarningClose}
      />
      <ConfirmModal
        {...expiredWarningDisclosureProps}
        hideCancel
        description={
          <Text>
            Please{" "}
            <Link href={subscriptionPath({ workspaceId })} onClick={handleExpiredWarningClose}>
              upgrade your plan
            </Link>{" "}
            to continue using Release
          </Text>
        }
        header="Environment Hours Expired"
        onConfirm={handleExpiredWarningClose}
      />
    </>
  );
}

export default WorkspaceLayout;
